import { Alert, Flex, Form, Input, message, Modal, Select, Steps, Typography } from "antd";
import styled from "styled-components";
import moment from "moment-timezone";
import { useMemo, useState } from "react";
import { usePostDeveloperPortalPersonalAccessTokensMutation } from "features/personalAccessTokensTable/personalAccessTokensAPI";
import { formatError } from "common/redux/middleware/queryErrorLogger";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 400px;
`;

function CreatePersonalAccessTokenModal({ title, open, onOk, onCancel }) {
  const [tokenValue, setTokenValue] = useState("");
  const [createPersonalAccessToken] = usePostDeveloperPortalPersonalAccessTokensMutation();

  const [form] = Form.useForm();

  const handleOk = async () => {
    if (tokenValue) {
      setTokenValue("");
      return onOk();
    }

    const values = await form.validateFields();
    const result = await createPersonalAccessToken(values);
    if ("data" in result) {
      form.resetFields();
      setTokenValue(result.data.token); // Set the token value to display
      message.success("Personal access token created successfully!");
    }
    if ("error" in result) {
      form.setFields(formatError(result.error));
    }
  };

  const handleCancel = () => {
    setTokenValue("");
    onCancel();
  };

  const expiresAtOptions = useMemo(() => {
    const now = moment();

    const makeOption = (label, days) => {
      const date = now.clone().add(days, "days").format("YYYY-MM-DD");
      return {
        label: `${label} (${date})`,
        value: date,
      };
    };

    return [
      {
        label: "No expiration",
        value: null,
      },
      makeOption("7 days", 7),
      makeOption("30 days", 30),
      makeOption("60 days", 60),
      makeOption("90 days", 90),
    ];
  }, []);

  return (
    <Modal
      maskClosable={false}
      title={title}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={tokenValue ? "Close" : "Generate"}
      cancelButtonProps={tokenValue && { style: { display: "none" } }}
    >
      <Flex vertical gap={12}>
        <Steps
          size="small"
          current={tokenValue ? 1 : 0}
          items={[
            {
              title: "Generate Token",
            },
            {
              title: "Copy Token",
            },
          ]}
        />
        {tokenValue ? (
          <>
            <Alert
              type="info"
              message="Please copy and save your personal access token and store it somewhere secure. This will not be shown again."
              showIcon
            />
            <Input
              value={tokenValue}
              readOnly
              suffix={<Typography.Text copyable={{ text: tokenValue }} />}
            />
          </>
        ) : (
          <StyledForm layout="vertical" form={form} autoComplete="off">
            <Form.Item label="Name" name="name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item label="Expiration Date" name="expiresAt">
              <Select options={expiresAtOptions} defaultValue={null} />
            </Form.Item>
          </StyledForm>
        )}
      </Flex>
    </Modal>
  );
}

export default CreatePersonalAccessTokenModal;
